import { Button } from "@/components/Button";
import classNames from "classnames";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import { Card } from "@/components/Card";
import MarisolFeatureList from "@/components/MarisolFeatureList";
import ReceivePayroll from "@/assets/Icons/receive-payroll.svg?react";
import DebitCard from "@/assets/Icons/debid-card.svg?react";
import AtmAccess from "@/assets/Icons/atm-access.svg?react";
import Support from "@/assets/Icons/support.svg?react";
import { useTranslation } from "react-i18next";
import MarisolMockEn from "@/assets/Img/Marisol/create-marisol-account-en.png";
import MarisolMockEs from "@/assets/Img/Marisol/create-marisol-account-es.png";

interface CreateAccountBannerProps {
  title: string;
  subtitle: string;
  disclaimer: string;
  buttonText: string;
  link?: string;
  hideOnMobile?: boolean;
  className?: string;
}

export const CreateAccountBanner = ({
  title,
  subtitle,
  disclaimer,
  buttonText,
  link = "/msfcu-bank-account",
  hideOnMobile = false,
  className,
}: CreateAccountBannerProps) => {
  const [t, i18n] = useTranslation("global");
  const lang = i18n.language;
  const featureLabels = t("Marisol.Features.Items", {
    returnObjects: true,
  }) as string[];

  return (
    <div
      id="bank-account"
      className={classNames("w-full", className, {
        "hidden lg:block": hideOnMobile,
      })}
    >
      <div className="flex flex-col lg:flex-row px-6 py-6">
        <div className="flex-1 flex flex-col items-center lg:justify-between lg:items-start lg:pr-16">
          <h2 className="text-3xl md:text-4xl text-center lg:text-left font-semibold">
            {title}
          </h2>
          <h3 className="text-base md:text-lg lg:text-2xl text-center lg:text-left">
            {subtitle}
          </h3>

          <div className="flex flex-row gap-2 py-8">
            <LockClosedIcon className="w-5 h-5 text-[#929292] font-normal" />
            <p className="text-base text-[#929292] font-normal">{disclaimer}</p>
          </div>

          <Button
            className="text-white !bg-[#214a39] !border-[#214a39] !font-light !px-10 !py-3"
            link={link}
            text={buttonText}
          />
        </div>

        {/* Marisol features */}
        <div className="flex-1 relative mt-14 lg:mt-0">
          <Card
            rest
            noPadding
            rounded="rounded-2xl"
            className="!bg-[#e7f2eb] border-4 border-[#e7f2eb] text-black p-5 md:max-w-none h-full"
          >
            <div className="flex items-stretch gap-4 h-full">
              <div className="flex-1 min-w-0 content-center">
                <MarisolFeatureList
                  cols={2}
                  className="gap-4 lg:gap-6 py-4"
                  features={[
                    {
                      icon: <ReceivePayroll className="w-10 h-10" />,
                      label: featureLabels[0],
                    },
                    {
                      icon: <DebitCard className="w-10 h-10" />,
                      label: featureLabels[1],
                    },
                    {
                      icon: <AtmAccess className="w-10 h-10" />,
                      label: featureLabels[2],
                    },
                    {
                      icon: <Support className="w-10 h-10" />,
                      label: featureLabels[3],
                    },
                  ]}
                />
              </div>

              <div className="hidden absolute md:relative h-full -mt-5 md:mt-0 right-3 md:flex self-stretch flex-shrink-0 items-center overflow-visible">
                {lang === "en" ? (
                  <img
                    src={MarisolMockEn}
                    alt="app preview"
                    className="max-h-64 md:max-h-96 lg:max-h-80 w-auto drop-shadow-2xl"
                  />
                ) : (
                  <img
                    src={MarisolMockEs}
                    alt="app preview"
                    className="max-h-64 md:max-h-96 w-auto drop-shadow-2xl"
                  />
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
